// URL contract for display-sized copies of our own R2 uploads. This is the
// single source of the shape the frontend builds `src`/`srcset` against — never
// inline these paths at call sites (à la the tripKey/placePhotoSrc factories).
//
// WHY this exists: cover/banner images are user uploads on our R2 CDN
// (`pub-<id>.r2.dev`) whose originals are multi-MB phone photos — far too heavy
// for a card or the LCP slot. The public `.r2.dev` domain can't resize on the
// fly (Cloudflare Transformations need a custom domain), so images.uploadImage
// writes a fixed ladder of WebP copies next to the original at upload time (see
// image-variants.ts), and the browser loads those straight from R2.
//
// History: until 2026-10 this pointed at an /api/img proxy that ran sharp on
// EVERY view. Cloudflare never cached it (no file extension on the path), so each
// page view re-decoded multi-MB originals in the one Node process serving the
// whole app. Resizing once at upload removed that per-view CPU/RAM entirely.
//
// Google place photos are NOT handled here — they keep healing through
// /api/place-photo (Google ToS forbids re-hosting their bytes); that proxy takes
// its own `w` param and rewrites the lh3 size suffix. See place-photo-url.ts.

// The widths written at upload. Small on purpose: every width is one more R2
// object per upload. 480 covers cards/thumbnails, 960 mid-size + 2x cards, 1600
// full-bleed banners. Changing this ladder requires re-running
// `pnpm backfill:image-variants` so existing uploads gain the new widths.
export const IMAGE_VARIANT_WIDTHS = [480, 960, 1600] as const

export type ImageVariantWidth = (typeof IMAGE_VARIANT_WIDTHS)[number]

// Only images.uploadImage uploads (`/images/...`) get variants. Item attachments
// (`/files/...`) and anything else on the bucket keep their single original, so
// they must never be pointed at a variant that doesn't exist. Host match is a
// suffix so this works client-side (no env access) and survives a bucket-id
// change; a future custom domain would be added here.
export function isResizableImageUrl(url: string | null | undefined): boolean {
  if (!url) return false
  try {
    const u = new URL(url)
    return u.hostname.endsWith(`.r2.dev`) && u.pathname.startsWith(`/images/`)
  } catch {
    return false
  }
}

// Snap an arbitrary requested width up to the nearest stored variant so callers
// can pass a raw slot width and still land on a file that exists.
export function snapImageWidth(width: number): ImageVariantWidth {
  for (const w of IMAGE_VARIANT_WIDTHS) {
    if (width <= w) return w
  }
  return IMAGE_VARIANT_WIDTHS[IMAGE_VARIANT_WIDTHS.length - 1]
}

// `images/u1/<uuid>.jpg` → `images/u1/<uuid>_w960.webp`. Works on an R2 object
// key or a full url path alike, so the uploader (keys) and the frontend (urls)
// derive the identical name.
export function imageVariantPath(path: string, width: ImageVariantWidth): string {
  const slash = path.lastIndexOf(`/`)
  const dot = path.lastIndexOf(`.`)
  const stem = dot > slash ? path.slice(0, dot) : path
  return `${stem}_w${width}.webp`
}

// The single src builder. For a resizable R2 upload, returns the stored variant
// that fits the requested width; for anything else (Unsplash, a data: url, a
// Google signed link) returns the url UNCHANGED so the caller's plain `src`
// still works.
export function resizedImageSrc(url: string, width: number): string {
  if (!isResizableImageUrl(url)) return url
  const u = new URL(url)
  u.pathname = imageVariantPath(u.pathname, snapImageWidth(width))
  return u.toString()
}

// Width-descriptor srcset over every stored variant so the browser pulls the one
// that fits the slot×DPR. Returns undefined for non-R2 urls so the caller falls
// back to its plain `src` (mirrors unsplashSrcSet's contract).
export function resizedImageSrcSet(url: string): string | undefined {
  if (!isResizableImageUrl(url)) return undefined
  return IMAGE_VARIANT_WIDTHS.map((w) => `${resizedImageSrc(url, w)} ${w}w`).join(`, `)
}
